import type { CardProps } from 'antd';
import { Alert, Card, Skeleton } from 'antd';
import React, { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
  DetailOperationLogs,
  type OperationLogInput,
} from '@/modules/zy-crm/components/detail-operation-logs';
import { DetailSectionPanel } from '@/modules/zy-crm/components/detail-section-panel';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import {
  type CrmScopedViewMode,
  resolveCrmScopedViewModeFromScope,
} from '@/modules/zy-crm/utils/crm-view';
import {
  type CrmPageScope,
  resolveCrmPageScopeFromPathname,
} from '@/modules/zy-crm/utils/data-scope';
import { queryContactDrawerProfile } from '@/services/bff/contact.api';
import { queryContractDrawerProfile } from '@/services/bff/contract.api';
import { queryLeadDrawerProfile } from '@/services/bff/lead.api';
import { queryQuoteDrawerProfile } from '@/services/bff/quote.api';

type DetailOperationLogBizType = 'lead' | 'contact' | 'contract' | 'quote';

type DetailOperationLogSectionProps = {
  bizType: DetailOperationLogBizType;
  bizUuid: string;
  title?: string;
  emptyText?: string;
  hint?: string;
  extra?: ReactNode;
  container?: 'section' | 'card';
  pageScope?: CrmPageScope;
  viewMode?: CrmScopedViewMode;
  cardProps?: CardProps;
  sectionProps?: {
    className?: string;
  };
};

const EMPTY_PAGE = {
  list: [],
  total: 0,
};

const BIZ_LOADERS: Record<
  DetailOperationLogBizType,
  (
    bizUuid: string,
    pageScope?: 'self' | 'all',
    viewMode?: CrmScopedViewMode,
  ) => Promise<OperationLogInput>
> = {
  lead: async (bizUuid, pageScope) => {
    const profile = await queryLeadDrawerProfile(bizUuid, {
      scope: pageScope,
    });
    return profile?.related?.operationLogs || EMPTY_PAGE;
  },
  contact: async (bizUuid, pageScope, viewMode) => {
    const profile = await queryContactDrawerProfile(bizUuid, {
      viewMode: viewMode || resolveCrmScopedViewModeFromScope(pageScope),
    });
    return profile?.related?.operationLogs || EMPTY_PAGE;
  },
  contract: async (bizUuid, pageScope, viewMode) => {
    const profile = await queryContractDrawerProfile(bizUuid, {
      viewMode: viewMode || resolveCrmScopedViewModeFromScope(pageScope),
    });
    return profile?.related?.operationLogs || EMPTY_PAGE;
  },
  quote: async (bizUuid, pageScope, viewMode) => {
    const profile = await queryQuoteDrawerProfile(bizUuid, {
      viewMode: viewMode || resolveCrmScopedViewModeFromScope(pageScope),
    });
    return profile?.related?.operationLogs || EMPTY_PAGE;
  },
};

export function DetailOperationLogSection({
  bizType,
  bizUuid,
  title = '操作记录',
  emptyText = '暂无操作记录',
  hint,
  extra,
  container = 'section',
  pageScope: pageScopeProp,
  viewMode,
  cardProps,
  sectionProps,
}: DetailOperationLogSectionProps) {
  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : '';
  const pageScope = pageScopeProp || resolveCrmPageScopeFromPathname(pathname);
  const { memberNameMap } = useMemberDirectory();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<OperationLogInput>(EMPTY_PAGE);
  const [loadError, setLoadError] = useState<Error>();

  const loadOperationLogs = useCallback(async () => {
    if (!bizUuid) {
      setItems(EMPTY_PAGE);
      setLoadError(undefined);
      return;
    }
    setLoading(true);
    setLoadError(undefined);
    try {
      const nextItems = await BIZ_LOADERS[bizType](
        bizUuid,
        pageScope,
        viewMode,
      );
      setItems(nextItems || EMPTY_PAGE);
    } catch (error) {
      setItems(EMPTY_PAGE);
      setLoadError(
        error instanceof Error ? error : new Error('操作记录加载失败'),
      );
    } finally {
      setLoading(false);
    }
  }, [bizType, bizUuid, pageScope, viewMode]);

  useEffect(() => {
    void loadOperationLogs();
  }, [loadOperationLogs]);

  const content = (
    <>
      {loading ? <Skeleton active paragraph={{ rows: 3 }} /> : null}
      {!loading && loadError ? (
        <Alert
          showIcon
          type="error"
          message="操作记录加载失败"
          description={loadError.message}
        />
      ) : null}
      {!loading && !loadError ? (
        <DetailOperationLogs
          title={title}
          items={items}
          emptyText={emptyText}
          memberNameMap={memberNameMap}
        />
      ) : null}
    </>
  );

  if (container === 'card') {
    return (
      <Card title={title} {...cardProps}>
        {content}
      </Card>
    );
  }

  return (
    <DetailSectionPanel
      title={title}
      hint={hint}
      extra={extra}
      className={sectionProps?.className}
    >
      {content}
    </DetailSectionPanel>
  );
}
